import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { ModalEditCustomerComponent } from '../modal-edit-customer/modal-edit-customer.component';
import { ModalSelectCustomerComponent } from '../modal-select-customer/modal-select-customer.component';
import { ModalSelectAddressComponent } from '../modal-select-address/modal-select-address.component';
import { DeferredAction } from 'app/shared/deferred-action';
import { Util } from 'app/shared/util';

declare const Mangler: any;

@Component({
	selector: 'modal-edit-project',
	templateUrl: './modal-edit-project.component.html',
	styleUrls: ['./modal-edit-project.component.scss']
})
export class ModalEditProjectComponent extends UIModalBaseComponent implements AfterViewInit, OnDestroy {

	@ViewChild(ModalEditCustomerComponent) customerEditModal: ModalEditCustomerComponent;
	@ViewChild(ModalSelectCustomerComponent) selectCustomerModal: ModalSelectCustomerComponent;
	@ViewChild('multiselectdropdown') multiselectdropdown!: ElementRef;
	@ViewChild(ModalSelectAddressComponent) addressPickerModal: ModalSelectAddressComponent;

	id;
	customerId;
	currencyId;
	data;
	disabled = false;
	siteDisabled = false;
	userList = [];
	addresses = [];

	selectedLabels = [];
	labelList = [];
	isDropdownOpen = false;
	autoCreateSite = false;
	siteName = null;
	autoSiteName = false;
	refreshAction = new DeferredAction();
	addressMatch = null;

	// One <app-modal-select-address> instance serves both the Project Location and the Invoicing
	// Details pickers, so addressPickerClosed() needs to know which one asked.
	addressPickerFor: 'site' | 'invoice' = 'site';

	// Which address the invoicing picker last returned, for the button label only. Deliberately a
	// component field rather than a property on data.project: saveButton() spreads the project into
	// the save payload, and `invoice_address_name` would sit one word away from `invoice_name`, the
	// real persisted addressee.
	invoiceAddressLabel = '';

	sub;

	get hasMatchingAddress() {
		if (!this.addresses) return false;
		if (!this.app.hasAddress(this.data.project)) return false;
		if (!this.addresses) return false;
		for (const contact of this.addresses) {
			if (this.app.compareAddress({ ...this.data.project, description: '' }, contact)) return true;
		}
		return false;
	};

	get hasAddress() {
		if (!this.addresses) return false;
		for (const contact of this.addresses) {
			if (this.app.hasAddress(contact)) return true;
		}
		return false;
	}

	get hasMatchingInvoiceAddress() {
		if (!this.addresses) return false;
		if (!this.app.hasAddress(this.data.project, 'invoice')) return false;
		// Cannot spread the project the way hasMatchingAddress does - compareAddress has no prefix
		// support, so the invoice_* fields have to be renamed first. Name is blanked because the
		// picker rows carry a provenance description, not a bill-to name.
		const current = { ...this.app.extractAddressFields(this.data.project, 'invoice_'), name: '', description: '' };
		for (const contact of this.addresses) {
			if (this.app.compareAddress(current, contact)) return true;
		}
		return false;
	}

	get overrideInvoiceDetails() { return !!this.data?.project?.invoice_details_override; }
	set overrideInvoiceDetails(value) {
		if (!this.data) return;
		this.data.project.invoice_details_override = value ? 1 : 0;
		if (value) this.fillInvoiceDefaultsIfEmpty();
	}

	get includeTravelCost() { return !!this.data.project.include_travel_on_cost; }
	set includeTravelCost(value) {
		if (this.data) {
			this.data.project.include_travel_on_cost = !!value;
		}
	}

	get portalAccess() { return !!+this.data?.project?.portal_access; }
	set portalAccess(value) {
		if (this.data) this.data.project.portal_access = value ? 1 : 0;
	}

	get autoCreateProjectSite() { return !!this.autoCreateSite; }
	set autoCreateProjectSite(value) {
		this.autoCreateSite = !!value;
	}

	constructor(
		public app: AppService,
		private api: ApiService,
	) {
		super();
	}

	ngAfterViewInit(): void {
		super.ngAfterViewInit();
		this.sub = this.modal.onHide.subscribe(() => {
			Util.blankAddressFields(this.data.project);
			Util.blankAddressFields(this.data.project, 'invoice');
		});
	}

	ngOnDestroy(): void {
		if (this.sub) this.sub.unsubscribe();
	}

	open(mdata: any) {
		this.id = mdata.id || 'new';
		this.customerId = mdata.customerId || null;
		this.currencyId = mdata.currencyId || null;
		this.data = null;
		this.disabled = false;
		this.isDropdownOpen = false;
		this.autoCreateSite = false;
		this.siteName = null;
		this.addresses = [];
		this.addressPickerFor = 'site';
		this.invoiceAddressLabel = '';

		const success = data => {
			this.data = data;
			this.userList = data.users;

			if (this.currencyId) {
				this.data.project.currency_id = this.currencyId;
			}

			if (this.app.orgInfo?.allow_procurement && this.id === 'new') {
				this.autoSiteName = true;
			}

			// Callers creating a project to share something on the portal (the send modals) start it switched on.
			if (this.id === 'new' && mdata.portalAccess) this.portalAccess = true;

			this.selectedLabels = Mangler.clone(data.project.labels);
			this.labelList = Mangler.clone(data.label_list);
			// Filter out labels from label_list that are already selected
			this.labelList = this.labelList.filter(label => (!this.selectedLabels.some(selectedLabel => selectedLabel.id === label.id)));
			this.modal.open();
			this.fetchAddresses();
		};

		const fail = error => {
			this.app.notifications.showDanger(error.message);
		};

		if (this.id === 'new') {
			this.api.project.new(this.customerId, success, fail);
		} else {
			this.api.project.get(this.id, success, fail);
		}
	}

	fetchAddresses() {
		if (this.data && this.data.project.customer_id) {
			this.api.customer.getCustomerAddress(this.data.project.customer_id, data => {
				this.addresses = data;
				// The customer's address is the prefill source, and it only arrives now - the toggle may
				// have been ticked before this landed, or the customer may just have changed.
				if (this.overrideInvoiceDetails) this.fillInvoiceDefaultsIfEmpty();
			}, error => {
				this.app.notifications.showDanger(error.message);
			});
		}
	}

	/**
	 * The customer's own Bill To, as an invoice would use it with no override. The name comes from the
	 * project payload; the address is the "Customer Address" row of the picker list, which
	 * api-customer.php::get_customer_address() reads from the same customer record
	 * Invoice::create_draft() does. Missing when the customer has no address on file, in which case
	 * only the name gets prefilled - which is the right outcome.
	 */
	customerBillTo() {
		const row = (this.addresses || []).find((a: any) => a.type === 'customer' && !a.sub_type);
		return { ...this.app.extractAddressFields(row, ''), name: this.data?.customer_name || '' };
	}

	// Only fills a completely untouched group, so re-ticking the toggle never overwrites the user's
	// edits or details already stored on the project.
	fillInvoiceDefaultsIfEmpty() {
		if (!this.data) return;
		const p = this.data.project;
		if (('' + (p.invoice_name || '')).trim() || this.app.hasAddress(p, 'invoice')) return;

		const src = this.customerBillTo();
		p.invoice_name = src.name;
		p.invoice_address_line_1 = src.address_line_1;
		p.invoice_address_line_2 = src.address_line_2;
		p.invoice_address_line_3 = src.address_line_3;
		p.invoice_posttown = src.posttown;
		p.invoice_county = src.county;
		p.invoice_postcode = src.postcode;
		p.invoice_country = src.country;
		this.invoiceAddressLabel = '';
	}

	useInvoiceAddress(addr) {
		if (!this.data) return;

		this.data.project.invoice_address_line_1 = addr.address_line_1;
		this.data.project.invoice_address_line_2 = addr.address_line_2;
		this.data.project.invoice_address_line_3 = addr.address_line_3;
		this.data.project.invoice_posttown = addr.posttown;
		this.data.project.invoice_county = addr.county;
		this.data.project.invoice_postcode = addr.postcode;
		this.data.project.invoice_country = addr.country;
		// Button label only. The picker rows describe where an address came from ("Customer Address",
		// "Contact John Smith, Jane Doe") - that is provenance, not a bill-to name, so Name is left to
		// the user.
		this.invoiceAddressLabel = addr.name || addr.description || addr.title || '';
	}

	// Changing the customer invalidates an override - it was an explicit statement about a different
	// billing entity, and silently repointing it risks billing the wrong party. Cleared here in the
	// modal rather than in save(), so the user sees it before committing and can cancel out.
	clearInvoiceOverride() {
		if (!this.data) return;

		this.data.project.invoice_details_override = 0;
		this.data.project.invoice_name = '';
		Util.blankAddressFields(this.data.project, 'invoice');
		this.invoiceAddressLabel = '';
	}

	useAddress(addr) {
		if (!this.data) return false;

		this.data.project.address_line_1 = addr.address_line_1;
		this.data.project.address_line_2 = addr.address_line_2;
		this.data.project.address_line_3 = addr.address_line_3;
		this.data.project.posttown = addr.posttown;
		this.data.project.county = addr.county;
		this.data.project.postcode = addr.postcode;
		this.data.project.country = addr.country;
		this.data.project.address_name = addr.name || addr.description || addr.title || '';
	}

	toggleSelection(label: any) {
		const index = this.selectedLabels.findIndex(selectedLabel => selectedLabel.id === label.id);
		if (index !== -1) {
			// Remove the label from selectedLabels and add it back to labelList
			this.selectedLabels.splice(index, 1);
			this.labelList.push(label); // Corrected variable name
		} else {
			// Add the label to selectedLabels and remove it from labelList
			this.selectedLabels.push(label);
			const labelIndex = this.labelList.findIndex(item => item.id === label.id); // Find index of label in label_list
			if (labelIndex !== -1) {
				this.labelList.splice(labelIndex, 1); // Remove label from label_list
			}
		}
	}

	toggleDropdown() {
		this.isDropdownOpen = !this.isDropdownOpen;
	}

	@HostListener('document:click', ['$event'])
	handleClickOutside(event: Event) {
		if (this.multiselectdropdown && !this.multiselectdropdown.nativeElement.contains(event.target)) {
			this.isDropdownOpen = false;
		}
	}

	saveButton() {
		if (!this.data) return;

		this.data.project.labels = this.selectedLabels;

		// Blank address lines are fine - the override is authoritative, blanks included - but an
		// invoice with no addressee is broken. Mirrored in api-project.php::save().
		if (this.overrideInvoiceDetails && !('' + (this.data.project.invoice_name || '')).trim()) {
			return this.app.notifications.showDanger('Please enter an invoicing name.');
		}

		if (this.id === 'new' && this.app.orgInfo?.allow_procurement && this.app.hasAddress(this.data.project)) {
			this.data.project.auto_create_project_site = this.autoCreateSite;
			if (!this.siteName && this.autoCreateSite) return this.app.notifications.showDanger('Site name is empty');
			if (this.autoCreateSite) this.data.project.project_site_name = this.siteName;
		}

		this.disabled = true;
		this.api.project.save(this.data.project, data => {
			this.disabled = false;
			this.app.notifications.showSuccess('Project saved.');
			// Blank the live address inputs before the modal closes so Chrome cannot
			// offer to save the project address as the user's personal address. The
			// payload was already sent above, so the save is unaffected. Both addresses
			// need it - two complete ones in a single form make Chrome likelier to ask.
			Util.blankAddressFields(this.data.project);
			Util.blankAddressFields(this.data.project, 'invoice');
			this.modal.close(data);
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	refreshCustomerName() {
		this.data.customer_name = '';
		if (!this.data.project.customer_id) return;

		this.api.customer.details(this.data.project.customer_id, data => {
			if (this.data.project.customer_id === data.record.id) {
				this.data.customer_name = data.record.name;
				// The name is half the prefill source, and refreshCustomerName() blanked it above.
				if (this.overrideInvoiceDetails) this.fillInvoiceDefaultsIfEmpty();
			}
		});

		this.app.tutorial?.validateStep(403);
	}

	addCustomer() {
		this.customerEditModal.open({ id: 'new' });
	}

	// A stored override belongs to the customer it was written for. The leading truthiness check keeps
	// a first-time selection (and a re-selection of the same customer) from clearing anything.
	customerChanged(id) {
		return !!this.data.project.customer_id && this.data.project.customer_id !== id;
	}

	customerEditModalClosed(id) {
		if (id) {
			if (this.customerChanged(id)) this.clearInvoiceOverride();
			this.data.project.customer_id = id;
			this.refreshCustomerName();
			this.fetchAddresses();
		}
	}

	selectCustomerModalClosed(data) {
		if (!data) return;

		if (this.customerChanged(data.id)) this.clearInvoiceOverride();
		this.data.project.customer_id = data.id;
		this.data.customer_name = data.name;
		this.fetchAddresses();
	}

	openAddressPicker() {
		if (!this.addresses?.length) return;

		this.addressPickerFor = 'site';
		const groups = this.app.buildAddressGroups(this.addresses);
		this.addressPickerModal.open({
			addressGroups: groups,
			preselectAddress: { ...this.app.extractAddressFields(this.data.project, ''), description: '' }
		});
	}

	openInvoiceAddressPicker() {
		if (!this.addresses?.length) return;

		this.addressPickerFor = 'invoice';
		const groups = this.app.buildAddressGroups(this.addresses);
		this.addressPickerModal.open({
			modalTitle: 'Select Invoicing Address',
			addressGroups: groups,
			preselectAddress: { ...this.app.extractAddressFields(this.data.project, 'invoice_'), name: '', description: '' }
		});
	}

	addressPickerClosed(sel: any | null) {
		// Reset before the early return - a cancelled invoicing pick must not leave the flag set for
		// the next Project Location pick to be misrouted into.
		const target = this.addressPickerFor;
		this.addressPickerFor = 'site';

		if (!sel) return;

		// The project_site handling below belongs to the procurement site-creation flow, so it must
		// stay off the invoicing branch.
		if (target === 'invoice') return this.useInvoiceAddress(sel);

		this.siteDisabled = false;
		this.useAddress(sel);
		if (sel.type === 'project_site') {
			this.autoCreateProjectSite = false;
			this.siteDisabled = true;
		} else this.checkAddressMatch();
	}

	descriptionChanged() {
		if (this.autoSiteName) this.siteName = this.data.project.description;
	}

	siteNameChanged() {
		this.autoSiteName = false;
	}

	checkAddressMatch(delay = 0) {
		this.addressMatch = null;
		if (!this.data || !this.data.project.customer_id) return;
		const deferredCount = this.refreshAction.run(delay, () => {
			this.refreshAction.wait();
			this.api.inventory.checkAddressMatchingSite(this.data.project, data => {
				this.refreshAction.release();
				if (deferredCount === this.refreshAction.count) {
					this.addressMatch = data;
					this.siteDisabled = false;
				}
			}, error => {
				if (deferredCount === this.refreshAction.count) {
					this.app.notifications.showDanger(error.message);
				}
			});
		});
	}
}
